import React, { useMemo } from "react";
import {
  Box,
  Dialog,
  DialogContent, Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent, { DataHeaderType } from "components/table/TableRowComponent";
import { WinterWheelSellingItem } from "../types/winter-wheel-price";

interface WheelImagesDetailModalProps {
  showWinterWheelModal: boolean;
  setShowWinterWheelModal: () => void;
  selectedWinterWheelSelling: WinterWheelSellingItem[];
}

const WINTER_WHEEL_PRICE_HEADER: DataHeaderType[] = [
  {
    label: "商品コード",
    sx: { width: "110px", textAlign: "center", px: "2px", fontSize: "15px" },
  },
  {
    label: "メーカー名",
    sx: { width: "240px", textAlign: "center", fontSize: "15px" },
  },
  {
    label: "ブランド名",
    sx: { width: "285px", textAlign: "center", fontSize: "15px" },
  },
  {
    label: "サイズ",
    sx: { width: "255px", textAlign: "center", fontSize: "15px" },
  },
  {
    label: "カラー",
    sx: { width: "255px", textAlign: "center", fontSize: "15px" },
  },
];

/**
 * WinterWheelPriceSettingModal Component
 *
 * This component renders a modal for filtering options related to nut, lock nut, and hub ring colors.
 * It provides radio button groups for selecting options and a button to apply the filter.
 * @param {object} props - The props for the component.
 * @param {boolean} props.showWinterWheelModal - Controls the visibility of the modal.
 * @param {(show: boolean) => void} props.setShowWinterWheelModal - Callback function to toggle the modal visibility.
 */
export default function WinterWheelPriceSettingModal({
  showWinterWheelModal,
  setShowWinterWheelModal,
  selectedWinterWheelSelling,
}: WheelImagesDetailModalProps) {
  const handleClose = () => {
    setShowWinterWheelModal();
  };

  const brandCdCellStyles = useMemo(
    () => ({
      border: "none",
      fontSize: { xs: "16px", lg: "18px" },
    }),
    [],
  );

  return (
    <Dialog
      open={showWinterWheelModal}
      onClose={handleClose}
      maxWidth="lg"
      PaperProps={{
        sx: { borderRadius: "21px" },
      }}
      fullWidth
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={{ xs: 2, md: 3, lg: 5 }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: "fontWeightMedium",
            color: "var(--gray-600)",
            fontSize: { xs: 28, md: 33 },
          }}
        >
          リスト
        </Typography>
        <CancelButton sx={{ color: "var(--gray-600)" }} onClose={handleClose} />
      </Box>
      <DialogContent sx={{ p: { xs: 2, md: 3, lg: 5 }, pt: "0 !important" }}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              minWidth: 1150,
              with: "100%",
              borderCollapse: "unset" as const,
              borderSpacing: "2px 1px",
              maxHeight: '73vh',
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRowComponent data={WINTER_WHEEL_PRICE_HEADER} type="header" />
              </TableHead>
              <TableBody>
                {selectedWinterWheelSelling.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={`${item.item_cd}-${index}`}>
                    {/* Brand Code Cell - Read Only */}
                    <TableCellComponent sx={brandCdCellStyles}>{item.item_cd}</TableCellComponent>
                    <TableCellComponent sx={{ ...brandCdCellStyles, textAlign: "left" }}>
                      {item.maker_name}
                    </TableCellComponent>
                    <TableCellComponent sx={{ ...brandCdCellStyles, textAlign: "left" }}>
                      {item.brand_name}
                    </TableCellComponent>
                    <TableCellComponent sx={brandCdCellStyles}>
                      {item.wheel_size}
                    </TableCellComponent>
                    <TableCellComponent sx={{ ...brandCdCellStyles, textAlign: "left" }}>
                      {item.wheel_color_name}
                    </TableCellComponent>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
